import React, { useState, useRef, useEffect } from "react";

interface Branch {
  id: number;
  name: string;
  [key: string]: any;
}

function normalizeBranchSearchKey(text: string): string {
  return String(text || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ");
}

interface BranchAutocompleteProps {
  branches: Branch[];
  value: number | null;
  onChange: (branchId: number) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  /** Valor al limpiar selección (por defecto -99). En reportes usar 0 para "Todas". */
  clearValue?: number;
}

const BranchAutocomplete: React.FC<BranchAutocompleteProps> = ({
  branches,
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar sucursal...",
  label = "Sucursal",
  clearValue = -99,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredBranches, setFilteredBranches] = useState<Branch[]>(branches);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredBranches(branches);
    } else {
      const query = normalizeBranchSearchKey(searchTerm);
      const filtered = branches.filter((branch) =>
        normalizeBranchSearchKey(branch.name).includes(query)
      );
      setFilteredBranches(filtered);
    }
  }, [searchTerm, branches]);

  const selectedBranch = branches.find((b) => b.id === value);
  const displayValue = selectedBranch ? selectedBranch.name : "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (branch: Branch) => {
    onChange(branch.id);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(clearValue);
    setSearchTerm("");
  };

  return (
    <div className="row mb-4 fs-13px col-12">
      <label className="form-label col-form-label col-12 col-md-4 text-start text-md-end">
        {label}:
      </label>
      <div className="col-12 col-md-8 col-lg-4" ref={dropdownRef}>
        <div className="position-relative">
          <div className="input-group">
            <div
              className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
              style={{ zIndex: 10 }}
            >
              <i className="fa fa-building opacity-5" />
            </div>
            <input
              type="text"
              className="form-control px-35px"
              style={{
                background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                fontSize: "13px",
                transition: "all 0.3s ease",
              }}
              placeholder={displayValue || placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsOpen(true)}
              disabled={disabled}
            />
            {value != null && value !== clearValue && !disabled && (
              <div
                className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 end-0"
                style={{ zIndex: 10, cursor: "pointer" }}
                onClick={handleClear}
                title="Limpiar selección"
              >
                <i className="fa fa-times text-muted" />
              </div>
            )}
          </div>

          {isOpen && !disabled && (
            <div
              className="position-absolute w-100 bg-white border rounded shadow-lg"
              style={{
                zIndex: 1000,
                maxHeight: "280px",
                overflowY: "auto",
                marginTop: "4px",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              }}
            >
              {filteredBranches.length > 0 ? (
                filteredBranches.map((branch) => (
                  <div
                    key={branch.id}
                    className="px-3 py-2"
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      borderBottom: "1px solid #f0f0f0",
                      fontSize: "13px",
                    }}
                    onClick={() => handleSelect(branch)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)";
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "white";
                      e.currentTarget.style.transform = "translateX(0)";
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className="fa fa-building me-2 text-primary"
                        style={{ fontSize: "12px" }}
                      />
                      <span className="fw-500">{branch.name}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className="px-3 py-3 text-center text-muted"
                  style={{ fontSize: "13px" }}
                >
                  <i className="fa fa-search me-2" />
                  No se encontraron sucursales
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchAutocomplete;
